'use client'

import {
    Exam,
    ExamStatus,
    EXAM_STATUS_LABELS,
    EXAM_STATUS_COLORS,
    getExamsByClient,
} from '@/lib/api/exams/exam-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdBiotech,
    MdPerson,
    MdAccessTime,
    MdShoppingCart,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import ExamRequestModal from '@/components/medical/exam-request-modal'
import { RecordActionsMenu } from '@/components/medical/record-actions-menu'
import { useProntuarioData, useProntuarioNavigate } from './prontuario-context'

export default function ExamesPage() {
    const { client, clientId } = useProntuarioData()
    const navigate = useProntuarioNavigate()

    const [exams, setExams] = useState<Exam[]>([])
    const [loading, setLoading] = useState(true)
    const [showRequestModal, setShowRequestModal] = useState(false)

    useEffect(() => {
        if (clientId) {
            loadExams()
        }
    }, [clientId])

    /**
     * @param silent recarrega SEM ligar o spinner.
     *
     * O spinner é um early return desta tela, e o modal de pedido é filho dela: ligar
     * `loading` com o modal aberto DESMONTA o modal e joga fora o estado dele. Era o
     * que acontecia ao emitir um pedido daqui — a etapa "Pronto" (imprimir, baixar,
     * enviar ao paciente) aparecia e sumia no mesmo instante, e o médico voltava para
     * a busca de exames achando que nada tinha acontecido.
     */
    const loadExams = async (silent = false) => {
        if (!clientId) return

        if (!silent) setLoading(true)
        try {
            const response = await getExamsByClient(parseInt(clientId, 10))
            if (response.success) {
                setExams(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar exames')
            }
        } catch (error) {
            toast.error('Erro ao carregar exames')
        } finally {
            if (!silent) setLoading(false)
        }
    }

    const getExamDisplayName = (exam: Exam): string => {
        if (exam.items && exam.items.length > 0) {
            const first = exam.items[0].catalog?.name || 'Item sem catálogo'
            const rest = exam.items.length - 1
            return rest > 0 ? `${first} +${rest}` : first
        }
        return exam.title
    }

    const formatCentsLabel = (cents: number): string => {
        return `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando exames...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">Exames</h2>
                    <p className="text-sm text-base-content/60">
                        Histórico de exames do paciente
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Pedido, não venda: o médico emite a ordem que o paciente leva
                        ao laboratório, sem preço nem cobrança. Vender exame continua
                        sendo trabalho da tela de agendamento. */}
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-1"
                        onClick={() => setShowRequestModal(true)}
                    >
                        <MdAdd className="text-base" />
                        Solicitar exames
                    </button>
                <span className="badge badge-lg badge-ghost">
                    {exams.length} {exams.length === 1 ? 'exame' : 'exames'}
                </span>
                </div>
            </div>

            {/* Exams List */}
            {exams.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdBiotech className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">Nenhum exame registrado</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {exams.map(exam => (
                        <div
                            key={exam.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => navigate(`/bookings/exams/${exam.id}`)}
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdBiotech className="text-xl text-secondary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-base-content truncate">
                                                    {getExamDisplayName(exam)}
                                                </h3>
                                                <p className="text-sm text-base-content/60">
                                                    {format(new Date(exam.requestedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <span className={`badge ${EXAM_STATUS_COLORS[exam.status]} text-white`}>
                                                {EXAM_STATUS_LABELS[exam.status]}
                                            </span>
                                            {exam.items && exam.items.length > 0 && (
                                                <span className="badge badge-ghost gap-1">
                                                    <MdShoppingCart className="text-sm" />
                                                    {exam.items.length} {exam.items.length === 1 ? 'item' : 'itens'}
                                                </span>
                                            )}
                                            {exam.results && exam.results.length > 0 && (
                                                <span className="badge badge-success text-white gap-1">
                                                    {exam.results.length} {exam.results.length === 1 ? 'resultado' : 'resultados'}
                                                </span>
                                            )}
                                        </div>

                                        {exam.description && (
                                            <p className="text-sm text-base-content/70 line-clamp-2 mb-3">
                                                {exam.description}
                                            </p>
                                        )}

                                        <div className="flex flex-wrap items-center gap-4 text-xs text-base-content/50">
                                            {exam.professional && (
                                                <div className="flex items-center gap-1">
                                                    <MdPerson className="text-sm" />
                                                    <span>{exam.professional.name}</span>
                                                </div>
                                            )}
                                            {exam.scheduledAt && (
                                                <div className="flex items-center gap-1">
                                                    <MdAccessTime className="text-sm" />
                                                    <span>Agendado: {format(new Date(exam.scheduledAt), 'dd/MM/yyyy', { locale: ptBR })}</span>
                                                </div>
                                            )}
                                            {exam.finalPrice != null && exam.finalPrice > 0 && (
                                                <span className="font-semibold text-secondary">
                                                    {formatCentsLabel(exam.finalPrice)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="ml-2 flex items-center gap-1">
                                        <RecordActionsMenu
                                            kind="exam"
                                            id={exam.id}
                                            clientEmail={client?.email}
                                        />
                                        <span
                                            className="btn btn-ghost btn-sm btn-square"
                                            title="Ver detalhes"
                                        >
                                            <MdArrowForward className="text-lg" />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showRequestModal && clientId && (
                <ExamRequestModal
                    clientId={parseInt(clientId, 10)}
                    clientName={client?.name}
                    clientEmail={client?.email}
                    onClose={() => setShowRequestModal(false)}
                    onCreated={() => loadExams(true)}
                />
            )}
        </div>
    )
}
